// ─── Types ────────────────────────────────────────────────────────────────────

export interface CampaignRow {
    id: string; name: string; status: string; objective: string;
    dailyBudget: number | null; lifetimeBudget: number | null;
    impressions: number | null; clicks: number | null; spend: number | null;
    cpm: number | null; cpc: number | null; ctr: number | null;
    reach: number | null; leads: number | null; purchases: number | null;
}

export interface AccountInsights {
    impressions: number; clicks: number; spend: number; cpm: number;
    cpc: number; ctr: number; reach: number; frequency: number;
    leads: number; purchases: number;
}

export interface AdAccount {
    id: string; name: string; currency: string; status: number;
    amountSpentTotal: number | null; insights: AccountInsights | null; campaigns: CampaignRow[];
}

export interface MetaData { period: string; adAccounts: AdAccount[]; totals: any; }

export interface ConversionEvent {
    id: string; timestamp: string; eventName: string;
    patientName: string; patientPhoneMasked: string;
    value: number | null; currency: string; status: 'sent' | 'error'; error?: string; orgId: string;
}

export interface AlertEntry {
    id: string; timestamp: string; campaignName: string; accountName: string;
    type: 'no_leads_48h' | 'high_cpl' | 'no_crm_conversion_7d'; detail: string; notified: boolean;
}

export interface AlertsConfig { enabled: boolean; cplThreshold: number; silenceHours: number; log: AlertEntry[]; }

export type InsightAction =
    | 'increase' | 'decrease' | 'pause' | 'scale'
    | 'bad_form' | 'creative_fatigue' | 'low_capture' | 'no_conversion';

export interface BudgetRec {
    campaignId: string; campaignName: string;
    accountId: string; accountName: string; currency: string;
    currentBudget: number; suggestedBudget: number;
    action: InsightAction;
    reason: string; priority: 'high' | 'medium' | 'low';
    spend: number; leads: number; cpl: number | null; avgCpl: number;
    leadsDb?: number; appts?: number;
}

export interface DianaReport {
    period7d:  { leads: number; appts: number; completed: number; convRate: number };
    period30d: { leads: number; appts: number; completed: number; convRate: number };
    allTime:   { leads: number };
    campaigns: { name: string; leads: number; appts: number; completed: number; convRate: number }[];
}

export interface ActiveCampaignRow {
    id: string; name: string; accountName: string; currency: string;
    spend: number | null; leads: number | null; cpl: number | null;
    dailyBudget: number | null; impressions: number | null; ctr: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const fmt = (n: number, d = 2) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : n.toFixed(d);

export const fmtUSD = (n: number, cur = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 2 }).format(n);

export const fmtTime = (iso: string) =>
    new Date(iso).toLocaleString('es-DO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function statusBadge(s: string) {
    switch (s?.toUpperCase()) {
        case 'ACTIVE':   return { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', label: 'Activa' };
        case 'PAUSED':   return { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',         label: 'Pausada' };
        case 'ARCHIVED': return { cls: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',            label: 'Archivada' };
        default:         return { cls: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',            label: s };
    }
}

export function accountStatusLabel(s: number) {
    switch (s) {
        case 1: return { label: 'Activa',    cls: 'text-emerald-500' };
        case 2: return { label: 'Inactiva',  cls: 'text-rose-500' };
        default: return { label: 'Otro',     cls: 'text-slate-400' };
    }
}
